import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { SessionActions } from '../../account/sessionSlice';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import Badge from '../../components/Badge';
import Button from '../../components/Button';
import Card, { Cards } from '../../components/Card';
import ConfirmationModal from '../../components/ConfirmationModal';
import Form, { Field, Submit } from '../../components/Form';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import SettingCard from '../../settings/SettingCard';
import SettingsSection from '../../settings/SettingsSection';
import SettingsSectionUpsell from '../../settings/SettingsSectionUpsell';
import { adminToasts } from '../../toasts/toasts';
import { UIActions } from '../../ui/uiSlice';
import getFeature from '../../utils/features';
import Link from '../../utils/router/Link';
import CustomDomain from './CustomDomain';
import * as styles from './CustomDomainSettings.module.scss';

const CUSTOM_DOMAIN_SETTINGS_CUSTOM_DOMAINS_FRAGMENT = gql(`
  fragment CustomDomainSettings_customDomains on CustomDomain {
    active
    hostname
    ...CustomDomain_customDomain
  }
`);

type CustomDomainFragment = DocumentType<
  typeof CUSTOM_DOMAIN_SETTINGS_CUSTOM_DOMAINS_FRAGMENT
>;

const VERIFY_CUSTOM_DOMAIN_MUTATION = gql(/** @complexity 2 */ `
  mutation verifyCustomDomain($hostname: ID!) {
    customDomain: verifyCustomDomain(hostname: $hostname) {
      ...CustomDomainSettings_customDomains
    }
  }
`);

interface CustomDomainSettingsProps {
  customDomains: CustomDomainFragment[];
  loading: boolean;
  host: string;
  effectiveHost: string;
  createCustomDomain: (hostname: string) => Promise<unknown>;
  deleteCustomDomain: (hostname: string) => Promise<unknown>;
  changeDefaultCustomDomain: (hostname: string, isDefault: boolean) => void;
}

const CustomDomainSettings = ({
  customDomains,
  loading,
  host,
  effectiveHost,
  createCustomDomain,
  deleteCustomDomain,
  changeDefaultCustomDomain,
}: CustomDomainSettingsProps) => {
  const isCustomDomainEnabled = getFeature('custom_domain');
  const dispatch = useAppDispatch();
  const [isAdding, setIsAdding] = useState(false);

  const addCustomDomain = ({ hostname }: { hostname: string }) =>
    createCustomDomain(hostname).then(() => {
      dispatch(UIActions.pushToast(adminToasts.customDomainAdded));
      setIsAdding(false);
    });

  const removeCustomDomain = ({ active, hostname }: CustomDomainFragment) => {
    if (active) {
      dispatch(
        UIActions.openModal(ConfirmationModal, {
          title: 'Remove domain?',
          description: `Slab will stop serving all requests for ${hostname}`,
          confirmButton: {
            text: 'Confirm',
            onClick: () =>
              deleteCustomDomain(hostname).then(() => {
                if (hostname === effectiveHost) {
                  dispatch(
                    SessionActions.setCurrentOrganization({
                      effectiveHost: host,
                    }),
                  );
                }
              }),
          },
        }),
      );
    } else {
      deleteCustomDomain(hostname);
    }
  };

  const [verifyCustomDomain] = useMutation(VERIFY_CUSTOM_DOMAIN_MUTATION);

  const refreshCustomDomain = ({ hostname }: CustomDomainFragment) =>
    verifyCustomDomain({ variables: { hostname } }).then((res) => {
      if (!res?.data?.customDomain.active) {
        dispatch(UIActions.pushToast(adminToasts.pendingDnsAndSslWarning));
      }
    });

  if (!isCustomDomainEnabled) {
    return (
      <SettingsSectionUpsell
        header="Custom Domains"
        feature="customDomains"
        description="Upgrade to set up a custom domain to use with your Slab account."
        contentCards={
          <Card>
            learn.yourcompany.com <Badge color="green">LIVE</Badge>
          </Card>
        }
      />
    );
  }

  return (
    <SettingsSection
      header="Custom Domains"
      subheader={
        !loading &&
        effectiveHost !== host && (
          <>
            Your Slab is also available at{' '}
            <Link to={`https://${host}`}>{host}</Link>.
          </>
        )
      }
    >
      {!loading && !isAdding && customDomains.length === 0 && (
        <div className={styles.empty}>None added</div>
      )}
      {(isAdding || customDomains.length > 0) && (
        <Cards>
          {customDomains.map((customDomain) => (
            <CustomDomain
              key={customDomain.hostname}
              customDomain={customDomain}
              isDefault={customDomain.hostname === effectiveHost}
              onChangeDefault={(isDefault) =>
                changeDefaultCustomDomain(customDomain.hostname, isDefault)
              }
              onRemove={() => removeCustomDomain(customDomain)}
              onRefresh={() => refreshCustomDomain(customDomain)}
            />
          ))}
          {isAdding && (
            <SettingCard
              description={
                <Form<{ hostname: string }>
                  initialValues={{ hostname: '' }}
                  onSubmit={addCustomDomain}
                  validateOnBlur={false}
                  validateOnChange={false}
                >
                  <div className={styles.fieldContainer}>
                    <Field
                      className={styles.field}
                      type="text"
                      id="hostname"
                      margin="none"
                      name="hostname"
                      placeholder="www.example.com"
                    />
                    <div className={styles.button}>
                      <Submit variant="primary">Save</Submit>
                    </div>
                    <div className={styles.button}>
                      <Button
                        variant="secondary"
                        onClick={() => setIsAdding(false)}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </Form>
              }
              title={<label htmlFor="hostname">Add a domain</label>}
            />
          )}
        </Cards>
      )}
      <div className={styles.add}>
        {!isAdding && (
          <Button variant="secondary" onClick={() => setIsAdding(true)}>
            Add
          </Button>
        )}
        <div className={styles.instructions}>
          <Link to="https://go.slab.com/create-a-custom-domain-cname" newTab>
            DNS configuration instructions
          </Link>
        </div>
      </div>
    </SettingsSection>
  );
};

export default CustomDomainSettings;
